import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from './useAuth'
import styles from './RegisterPage.module.css'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function buildGoogleOAuthUrl(): string {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? ''
  const redirectUri = `${window.location.origin}/auth/callback`
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'email profile',
    access_type: 'offline',
    prompt: 'select_account',
  })
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
}

export function RegisterPage() {
  const { register } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string; confirm?: string }>({})
  const [serverError, setServerError] = useState<string | undefined>()
  const [loading, setLoading] = useState(false)

  const clearFieldError = (field: string) =>
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }))

  const validateForm = (): boolean => {
    const errs: typeof fieldErrors = {}
    if (!email.trim()) errs.email = 'El correo es obligatorio.'
    else if (!EMAIL_REGEX.test(email.trim())) errs.email = 'Ingresa un correo válido.'
    if (!password) errs.password = 'La contraseña es obligatoria.'
    else if (password.length < 8) errs.password = 'Mínimo 8 caracteres.'
    if (!confirmPassword) errs.confirm = 'Confirma tu contraseña.'
    else if (password !== confirmPassword) errs.confirm = 'Las contraseñas no coinciden.'
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setServerError(undefined)
    if (!validateForm()) return
    setLoading(true)
    try {
      await register(email, password)
      navigate('/', { replace: true })
    } catch (err) {
      const msg = err instanceof Error ? err.message : ''
      if (msg.includes('already') || msg.includes('existe') || msg.includes('422')) {
        setServerError('Ya existe una cuenta con ese correo.')
      } else if (msg.includes('conexión') || msg.includes('internet')) {
        setServerError('Sin conexión. Verifica tu internet.')
      } else {
        setServerError('No pudimos crear tu cuenta. Intenta nuevamente.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.page}>
      {/* Left panel — brand */}
      <div className={styles.brand}>
        <div className={styles.brandInner}>
          <div className={styles.brandLogo}>
            <img src="/icono-app-ui.svg" alt="Fásico" className={styles.brandIsotipo} />
            <span className={styles.brandName}>Fásico</span>
          </div>
          <p className={styles.brandTagline}>Documentación TE1 para electricistas SEC</p>
          <div className={styles.brandFeatures}>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>⚡</span>
              <span>13 documentos generados automáticamente</span>
            </div>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>📋</span>
              <span>Conforme a DS 08/2019 RIC</span>
            </div>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>📱</span>
              <span>Funciona en tu celular Android</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className={styles.formPanel}>
        <div className={styles.formInner}>
          {/* Logo mobile */}
          <div className={styles.mobileHeader}>
            <img src="/icono-app-ui.svg" alt="" aria-hidden="true" className={styles.mobileIsotipo} />
            <span className={styles.mobileBrand}>Fásico</span>
          </div>

          <h1 className={styles.title}>Crear cuenta</h1>
          <p className={styles.subtitle}>Regístrate gratis y comienza hoy</p>

          {serverError && (
            <div className={styles.errorBox} role="alert">{serverError}</div>
          )}

          <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="email">Correo electrónico</label>
              <input
                id="email"
                type="email"
                className={`${styles.input} ${fieldErrors.email ? styles.inputError : ''}`}
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearFieldError('email') }}
                placeholder="tu@email.com"
                autoComplete="email"
                required
              />
              {fieldErrors.email && <span className={styles.fieldError}>{fieldErrors.email}</span>}
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="password">Contraseña</label>
              <input
                id="password"
                type="password"
                className={`${styles.input} ${fieldErrors.password ? styles.inputError : ''}`}
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearFieldError('password') }}
                placeholder="Mínimo 8 caracteres"
                autoComplete="new-password"
                required
              />
              {fieldErrors.password && <span className={styles.fieldError}>{fieldErrors.password}</span>}
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="confirmPassword">Confirmar contraseña</label>
              <input
                id="confirmPassword"
                type="password"
                className={`${styles.input} ${fieldErrors.confirm ? styles.inputError : ''}`}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); clearFieldError('confirm') }}
                placeholder="Repite tu contraseña"
                autoComplete="new-password"
                required
              />
              {fieldErrors.confirm && <span className={styles.fieldError}>{fieldErrors.confirm}</span>}
            </div>

            <button type="submit" className={styles.btnPrimary} disabled={loading}>
              {loading ? 'Creando cuenta...' : 'Crear cuenta gratis'}
            </button>
          </form>

          <div className={styles.divider}>
            <span className={styles.dividerLine} />
            <span className={styles.dividerText}>o</span>
            <span className={styles.dividerLine} />
          </div>

          <button type="button" className={styles.btnGoogle} onClick={() => { window.location.href = buildGoogleOAuthUrl() }}>
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            Continuar con Google
          </button>

          <p className={styles.login}>
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className={styles.loginLink}>Inicia sesión</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
